import { IElementCategory } from '@shared/models/element-categories.model';
import { IConditionalNode } from '../../../helpers/conditional-nodes.helpers';
import { pick } from '../../../helpers/object.helpers';
import {
  IFactorySelectProperty,
  ElementPropertyType,
  IFactorySwitchProperty,
  IFactoryProperty,
} from '../../../models/element-property.model';
import { commonElementCategoryProperties } from '../common-element-category-properties.model';
import { reusedContentProperty } from '../reused-content-property.model';
import {
  WindowsDoorsPropertyName,
  WindowsDoorsCategory,
  Opening,
  FrameMaterial,
  Glazing,
  WindowsDoorsType,
} from './windows-doors.model';

export const windowsDoorsCategoryProperty: IFactorySelectProperty = {
  name: WindowsDoorsPropertyName.WindowsDoorsCategory,
  type: ElementPropertyType.Select,
  count: WindowsDoorsCategory.Window,
  options: Object.values(WindowsDoorsCategory),
};

const opening: IFactorySelectProperty = {
  name: WindowsDoorsPropertyName.Opening,
  type: ElementPropertyType.Select,
  options: Object.values(Opening),
  indentation: 1,
};

const glazing: IFactorySelectProperty = {
  name: WindowsDoorsPropertyName.Glazing,
  type: ElementPropertyType.Select,
  options: Object.values(Glazing),
  indentation: 1,
};

const fireRated: IFactorySwitchProperty = {
  name: WindowsDoorsPropertyName.FireRated,
  type: ElementPropertyType.Switch,
  indentation: 1,
};

const framType: IFactorySelectProperty = {
  name: WindowsDoorsPropertyName.WindowsDoorsType,
  type: ElementPropertyType.Select,
  options: Object.values(WindowsDoorsType),
  indentation: 1,
};

const frameMaterial: IFactorySelectProperty = {
  type: ElementPropertyType.Select,
  name: WindowsDoorsPropertyName.FrameMaterial,
  indentation: 1,
};

export const windowConditionalProperty: IConditionalNode<IFactorySelectProperty> =
  {
    conditions: {
      [WindowsDoorsPropertyName.WindowsDoorsCategory]:
        WindowsDoorsCategory.Window,
    },
    nodes: [
      opening,
      {
        ...frameMaterial,
        count: FrameMaterial.WoodAluminum,
        options: Object.values(
          pick(FrameMaterial, 'WoodAluminum', 'Wood', 'Aluminum'),
        ),
      },
    ],
  };

const glazingConditions: IConditionalNode<IFactorySelectProperty>[] = [
  {
    conditions: {
      [WindowsDoorsPropertyName.Glazing]: Glazing.Full,
    },
    nodes: [
      {
        ...frameMaterial,
        options: Object.values(
          pick(FrameMaterial, 'WoodAluminum', 'Wood', 'Aluminum'),
        ),
      },
    ],
  },
  {
    conditions: {
      [WindowsDoorsPropertyName.Glazing]: Glazing.Semi,
    },
    nodes: [
      {
        ...frameMaterial,
        options: Object.values(pick(FrameMaterial, 'WoodAluminum', 'Wood')),
      },
    ],
  },
  {
    conditions: {
      [WindowsDoorsPropertyName.Glazing]: Glazing.None,
    },
    nodes: [
      {
        ...frameMaterial,
        options: Object.values(
          pick(FrameMaterial, 'Wood', 'SteelStainless', 'SteelCarbon'),
        ),
      },
    ],
  },
];

export const doorExternalConditionalProperty: IConditionalNode<IFactorySelectProperty> =
  {
    conditions: {
      [WindowsDoorsPropertyName.WindowsDoorsCategory]:
        WindowsDoorsCategory.DoorExternal,
    },
    nodes: [glazing, ...glazingConditions],
  };

const fireRatedConditions: IConditionalNode<IFactoryProperty>[] = [
  {
    conditions: {
      [WindowsDoorsPropertyName.FireRated]: false,
    },
    nodes: [
      {
        ...frameMaterial,
        options: Object.values(pick(FrameMaterial, 'Wood')),
      },
    ],
  },
  {
    conditions: {
      [WindowsDoorsPropertyName.FireRated]: true,
    },
    nodes: [
      {
        ...frameMaterial,
        options: Object.values(
          pick(FrameMaterial, 'Wood', 'WoodLaminated', 'SteelCarbon'),
        ),
      },
    ],
  },
];

export const doorInternalConditionalProperty: IConditionalNode<IFactoryProperty> =
  {
    conditions: {
      [WindowsDoorsPropertyName.WindowsDoorsCategory]:
        WindowsDoorsCategory.DoorInternal,
    },
    nodes: [fireRated, ...fireRatedConditions],
  };

export const glassGoodsConditionalProperty: IConditionalNode<IFactorySelectProperty> =
  {
    conditions: {
      [WindowsDoorsPropertyName.WindowsDoorsCategory]:
        WindowsDoorsCategory.GlassGoods,
    },
    nodes: [framType],
  };

export const windowsDoorsProperties: IElementCategory['properties'] = [
  windowsDoorsCategoryProperty,
  windowConditionalProperty,
  doorExternalConditionalProperty,
  doorInternalConditionalProperty,
  glassGoodsConditionalProperty,
  reusedContentProperty,
  ...commonElementCategoryProperties,
];
